import { HttpError, useList } from "@refinedev/core";
import { useEffect, useState, useMemo } from "react";
import { calendar_v3 } from "googleapis";
import dayjs, { Dayjs } from "dayjs";
import utc from 'dayjs/plugin/utc';
import { logger } from "@utils/logger";
import { APPOINTMENT_TIME_MINUTES } from "core/calendar/definitions";

// Configure dayjs to use UTC
dayjs.extend(utc);

/**
 * Custom hook to manage and calculate available appointment slots for professionals
 * by comparing their availability calendar with their existing appointments.
 * 
 * @param availabilityCalendarIds - Array of Google Calendar IDs representing when the professional is available
 * @param appointmentsCalendarIds - Array of Google Calendar IDs containing existing appointments
 * @param startDate - The start date/time to search for available slots
 * @param endDate - The end date/time to search for available slots
 * 
 * @returns Object containing:
 *   - data: Array of arrays of available appointment slots (Date objects)
 *   - ...rest: Additional properties from useList hook (isLoading, isError, etc.)
 * 
 * @throws Error if availabilityCalendarIds and appointmentsCalendarIds have different lengths
 */
export const useProfessionalCalendar = (availabilityCalendarIds: string[], appointmentsCalendarIds: string[], startDate: Date | Dayjs, endDate: Date | Dayjs) => {
    // State to store the calculated available slots
    const [availableSlots, setAvailableSlots] = useState<Date[][]>([]);

    // Input validation
    // if (!availabilityCalendarIds?.length || !appointmentsCalendarIds?.length) {
    //     throw new Error('Calendar IDs arrays cannot be empty');
    // }

    if (availabilityCalendarIds.length !== appointmentsCalendarIds.length) {
        throw new Error('Availability and appointments calendar ids must have the same length');
    }

    if (!startDate || !endDate) {
        throw new Error('Start and end dates are required');
    }

    if (dayjs(startDate).isAfter(dayjs(endDate))) {
        throw new Error('Start date must be before end date');
    }

    // Combine all calendar IDs for the API request
    const totalCalendarIds = [...availabilityCalendarIds, ...appointmentsCalendarIds];

    // Fetch calendar data using Refine's useList hook
    const {
        data: calendarEvents,
        ...rest
    } = useList<calendar_v3.Schema$FreeBusyRequest, HttpError, calendar_v3.Schema$FreeBusyResponse>({
        dataProviderName: 'gapi',
        resource: `calendar/freeBusy`,
        meta: {
            method: 'post',
            body: {
                timeMin: dayjs(startDate).utc().toISOString(),
                timeMax: dayjs(endDate).utc().toISOString(),
                items: totalCalendarIds?.map((id) => ({ id })),
            }
        },
        queryOptions: {
            enabled: !!totalCalendarIds && totalCalendarIds.length > 0
        },
    });

    // Extract and separate availability and appointment calendars
    const totalCalendars = useMemo(() => 
        calendarEvents?.calendars as Record<string, calendar_v3.Schema$FreeBusyCalendar> || {}, 
        [calendarEvents?.calendars]
    );
    
    const availabilityCalendars = useMemo(() => 
        filterKeys(totalCalendars, availabilityCalendarIds), 
        [totalCalendars, availabilityCalendarIds]
    );
    
    const appointmentCalendars = useMemo(() => 
        filterKeys(totalCalendars, appointmentsCalendarIds), 
        [totalCalendars, appointmentsCalendarIds]
    );

    // Calculate available slots whenever calendar data changes
    useEffect(() => {
        try {
            // Map each availability calendar to its corresponding appointment calendar
            const slots = availabilityCalendars
                .map((availableCalendar, i) => {
                    if (!availableCalendar || !appointmentCalendars[i]) {
                        logger.info('Missing calendar data for index', i);
                        return [];
                    }
                    return findAvailableSlots(availableCalendar, appointmentCalendars[i]);
                });
            setAvailableSlots(slots);
        } catch (error) {
            logger.error('Error calculating available slots:', error);
            setAvailableSlots([]);
        }
    }, [availabilityCalendars, appointmentCalendars]);

    return {
        data: availableSlots,
        ...rest
    };
};

/**
 * Finds available appointment slots by comparing availability calendar with busy periods
 * 
 * @param availableCalendar - Calendar containing available time slots
 * @param busyCalendar - Calendar containing busy periods
 * @param slotDurationMinutes - Duration of each appointment slot in minutes (defaults to APPOINTMENT_TIME_MINUTES)
 * 
 * @returns Array of available appointment slots as Date objects
 */
const findAvailableSlots = (
    availableCalendar: calendar_v3.Schema$FreeBusyCalendar,
    busyCalendar: calendar_v3.Schema$FreeBusyCalendar,
    slotDurationMinutes: number = APPOINTMENT_TIME_MINUTES,
) => {
    if (!availableCalendar?.busy || !busyCalendar?.busy) {
        return [];
    }

    // Generate all possible time slots from available periods
    // Each slot is slotDurationMinutes long
    const availableSlots = availableCalendar.busy.map(event => {
        if (!event.start || !event.end) {
            return [];
        }

        const slots: dayjs.Dayjs[] = [];
        let currentSlot = dayjs(event.start).utc();
        const eventEnd = dayjs(event.end).utc();

        // Validate dates
        if (!currentSlot.isValid() || !eventEnd.isValid()) {
            logger.info('Invalid date in calendar event:', { start: event.start, end: event.end });
            return [];
        }

        // Create slots of slotDurationMinutes until we reach the end of the available period
        while (currentSlot.isBefore(eventEnd)) {
            slots.push(currentSlot);
            currentSlot = currentSlot.add(slotDurationMinutes, 'minutes');
        }
        return slots;
    }).flat();

    // Return empty array if no available slots found
    if (availableSlots.length === 0) {
        return [];
    }

    // Convert busy periods to start-end pairs for easier comparison
    const busyLimits = busyCalendar.busy
        .filter(event => event.start && event.end)
        .map(event => ({
            start: dayjs(event.start).utc(),
            end: dayjs(event.end).utc()
        }))
        .filter(limit => limit.start.isValid() && limit.end.isValid());

    // Get the overall time range we're working with
    const availableSlotStart = availableSlots[0];
    const availableSlotEnd = availableSlots[availableSlots.length - 1];

    if (!availableSlotStart || !availableSlotEnd) {
        return [];
    }

    const freeSlots: Date[] = [];
    let currentSlot = availableSlotStart;
    let currentAvailableLimitIndex = 0;

    // Iterate through all possible slots and check if they're actually free
    while (currentSlot.isBefore(availableSlotEnd) || currentSlot.isSame(availableSlotEnd)) {
        // Check if current slot is in the available slots list
        if (currentAvailableLimitIndex < availableSlots.length &&
            availableSlots[currentAvailableLimitIndex].isSame(currentSlot)) {
            let busy = false;
            // Check if the slot overlaps with any busy period
            for (const busyLimit of busyLimits) {
                // Skip past busy periods that have ended
                if (currentSlot.isAfter(busyLimit.end) || currentSlot.isSame(busyLimit.end)) {
                    continue;
                }

                // Check if slot overlaps with current busy period
                if (currentSlot.isAfter(busyLimit.start) ||
                    currentSlot.add(slotDurationMinutes, 'minutes').isAfter(busyLimit.start)) {
                    busy = true;
                    break;
                }
            }

            // If slot is not busy, add it to free slots
            if (!busy) {
                freeSlots.push(currentSlot.toDate());
            }
            currentSlot = currentSlot.add(slotDurationMinutes, 'minutes');
        } else {
            // Move to next available slot if current slot is not in available slots
            currentAvailableLimitIndex++;
            if (currentAvailableLimitIndex >= availableSlots.length) {
                break;
            }
            currentSlot = availableSlots[currentAvailableLimitIndex];
        }
    }

    return freeSlots;
};

/**
 * Filters an object to only include entries with specified keys
 * 
 * @param obj - The object to filter
 * @param keys - Array of keys to keep
 * @returns Array of values for the specified keys
 */
function filterKeys<V>(obj: Record<string, V>, keys: string[]) {
    if (!obj || !keys?.length) {
        return [];
    }
    // Convert object to entries, filter by keys, and map back to values
    return Object.entries(obj)
        .filter(([key]) => keys.includes(key))
        .map(([_, value]) => value);
}